// admin/src/pages/GatewayMonitoring/components/DeleteConfirmDialog.js
'use strict';

import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  TextField,
  Alert,
  CircularProgress,
  Stack,
} from '@mui/material';
import BlockIcon from '@mui/icons-material/Block';
import DeleteForeverIcon from '@mui/icons-material/DeleteForever';
import { revokeGateway, deleteGateway, extractGatewayErrorMessage } from '../gatewayApi';

export default function DeleteConfirmDialog({
  open = false,
  gateway = null,
  actionType = 'revoke', // 'revoke' | 'delete'
  onClose,
  onSuccess,
}) {
  const [typedName, setTypedName] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    setTypedName('');
    setError(null);
  }, [open, gateway]);

  if (!gateway) return null;

  const isDelete = actionType === 'delete';
  const nameMatches = typedName.trim() === gateway.name.trim();

  const handleConfirm = async () => {
    if (isDelete && !nameMatches) return;
    setSubmitting(true);
    setError(null);
    try {
      if (isDelete) {
        await deleteGateway(gateway._id);
      } else {
        await revokeGateway(gateway._id);
      }
      onSuccess && onSuccess();
      onClose();
    } catch (err) {
      setError(extractGatewayErrorMessage(err));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="xs"
      fullWidth
      data-testid={isDelete ? 'delete-gateway-dialog' : 'revoke-gateway-dialog'}
    >
      <DialogTitle sx={{ fontWeight: 700, display: 'flex', alignItems: 'center', gap: 1 }}>
        {isDelete ? (
          <>
            <DeleteForeverIcon color="error" />
            Delete Gateway
          </>
        ) : (
          <>
            <BlockIcon color="warning" />
            Revoke Gateway Credentials
          </>
        )}
      </DialogTitle>

      <DialogContent>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        <Stack spacing={2}>
          {isDelete ? (
            <>
              <Typography variant="body2">
                This will soft-delete the gateway record <strong>{gateway.name}</strong> and immediately revoke its authentication credentials.
              </Typography>
              <Typography variant="body2" color="error.main" sx={{ fontWeight: 600 }}>
                To confirm deletion, please type the exact gateway name <strong>{gateway.name}</strong> below:
              </Typography>
              <TextField
                fullWidth
                size="small"
                label="Gateway Name"
                placeholder={gateway.name}
                data-testid="delete-confirm-typed-input"
                value={typedName}
                onChange={(e) => setTypedName(e.target.value)}
              />
            </>
          ) : (
            <>
              <Typography variant="body2">
                Are you sure you want to revoke credentials for gateway <strong>{gateway.name}</strong>?
              </Typography>
              <Typography variant="body2" color="text.secondary">
                The gateway will no longer be able to check in or push log events. Previously shipped telemetry events will be retained according to your retention policy.
              </Typography>
            </>
          )}
        </Stack>
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 2.5 }}>
        <Button onClick={onClose} variant="outlined" disabled={submitting}>
          Cancel
        </Button>
        <Button
          variant="contained"
          color={isDelete ? 'error' : 'warning'}
          disabled={submitting || (isDelete && !nameMatches)}
          data-testid="execute-confirm-btn"
          onClick={handleConfirm}
        >
          {submitting ? (
            <CircularProgress size={18} color="inherit" />
          ) : isDelete ? (
            'Delete Gateway'
          ) : (
            'Revoke Credentials'
          )}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
